import { useCallback, useMemo, useState } from 'react';
import { SlotInfo, Event, View, Views } from 'react-big-calendar';

import { Campaign, CampaignSlot } from 'src/api/campaigns/types';
import { useGlobalModals } from 'src/contexts/global-modals';
import { useEditCampaignSlotMutation } from 'src/api/campaigns/hooks';
import { useNavigate } from 'react-router-dom';

export interface CampaignEvent extends Event {
  slot: CampaignSlot;
}

export const useCampaignCalendar = (data: Campaign) => {
  const navigate = useNavigate();

  const [selectedSlot, setSelectedSlot] = useState<SlotInfo | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<View>(Views.MONTH);

  const { openLoadingModal, closeLoadingModal } = useGlobalModals();
  const { editCampaignSlotMutation } = useEditCampaignSlotMutation();

  const campaignStartDate = new Date(data.date_from);
  const campaignEndDate = new Date(data.date_to);

  const handleSelecting = useCallback(
    (range: { start: Date; end: Date }) => {
      const { start, end } = range;
      const now = new Date();

      if (start < now) {
        return false;
      }

      const isStartValid =
        start >= campaignStartDate && start <= campaignEndDate;
      const isEndValid = end >= campaignStartDate && end <= campaignEndDate;

      if (!isStartValid || !isEndValid) {
        return false;
      }

      return true;
    },
    [campaignStartDate, campaignEndDate]
  );

  const events = useMemo(
    (): CampaignEvent[] =>
      data.slots.map((slot) => ({
        id: slot.id,
        title: `${slot.name} - ${slot.device.name}, ${slot.interaction.name}, ${slot.branding.name}`,
        start: new Date(slot.date_from),
        end: new Date(slot.date_to),
        slot: slot,
      })),
    [data.slots]
  );

  const handleNavigation = (newDate: Date) => setCurrentDate(newDate);
  const handleViewChange = (newView: View) => setCurrentView(newView);

  const handleAddEvent = (slotInfo: SlotInfo) => {
    const start = slotInfo.start;
    const end = slotInfo.end;

    if (start < campaignStartDate || end > campaignEndDate) {
      return;
    }

    setSelectedSlot(slotInfo);
    setIsAddModalOpen(true);
  };

  const handleDateClick = (date: Date) => {
    handleNavigation(date);
    handleViewChange('day');
  };

  const handleSelectEvent = useCallback((event: Event) => {
    const campaignEvent = event as CampaignEvent;
    if (campaignEvent.slot) {
      navigate(`/campaign/${data.id}/${campaignEvent.slot.id}`);
    }
  }, []);

  const handleSelectSlot = (slotInfo: SlotInfo) => {
    if (slotInfo.action !== 'select') {
      return;
    }
    const start = slotInfo.start;
    const end = slotInfo.end;
    const now = new Date();

    if (start < now) {
      return;
    }

    if (start < campaignStartDate || end > campaignEndDate) {
      return;
    }

    setSelectedSlot(slotInfo);
    setIsAddModalOpen(true);
  };

  const handleEventDrop = useCallback(
    async (event: {
      event: Event;
      start: Date | string;
      end: Date | string;
    }) => {
      const campaignEvent = event.event as CampaignEvent;
      if (!campaignEvent?.slot || !event?.start || !event?.end) {
        return;
      }

      openLoadingModal(
        'Updating campaign slot',
        'Please wait while we update the campaign slot...'
      );

      try {
        await editCampaignSlotMutation({
          id: campaignEvent.slot.id,
          data: {
            date_from: new Date(event.start).toISOString(),
            date_to: new Date(event.end).toISOString(),
          },
        });
      } finally {
        closeLoadingModal();
      }
    },
    [editCampaignSlotMutation, openLoadingModal, closeLoadingModal]
  );

  const handleEventResize = useCallback(
    async (event: {
      event: Event;
      start: Date | string;
      end: Date | string;
    }) => {
      const campaignEvent = event.event as CampaignEvent;
      if (!campaignEvent?.slot || !event?.start || !event?.end) {
        return;
      }

      openLoadingModal(
        'Updating slot duration',
        'Please wait while we update the slot duration...'
      );

      try {
        await editCampaignSlotMutation({
          id: campaignEvent.slot.id,
          data: {
            date_from: new Date(event.start).toISOString(),
            date_to: new Date(event.end).toISOString(),
          },
        });
      } finally {
        closeLoadingModal();
      }
    },
    [editCampaignSlotMutation, openLoadingModal, closeLoadingModal]
  );

  const closeModal = useCallback(() => {
    setIsAddModalOpen(false);
    setSelectedSlot(null);
  }, []);

  return {
    selectedSlot,
    isAddModalOpen,
    currentDate,
    currentView,

    events,
    campaignStartDate,
    campaignEndDate,

    handleNavigation,
    handleViewChange,
    handleAddEvent,
    handleSelectSlot,
    handleSelectEvent,
    handleSelecting,
    handleEventDrop,
    handleEventResize,
    handleDateClick,
    closeModal,
  };
};
